"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Globe, Search, Copy, Loader2, Server, Mail, FileText, Shield } from "lucide-react"
import { toast } from "sonner"

interface DnsRecord {
  type: string
  value: string | string[]
  ttl?: number
  priority?: number
}

interface DnsResult {
  domain: string
  records: {
    A?: DnsRecord[]
    AAAA?: DnsRecord[]
    CNAME?: DnsRecord[]
    MX?: DnsRecord[]
    TXT?: DnsRecord[]
    NS?: DnsRecord[]
    SOA?: DnsRecord[]
  }
  timing: number
}

const recordTypeInfo: Record<string, { icon: typeof Server; color: string; description: string }> = {
  A: { icon: Server, color: "bg-blue-500", description: "IPv4 Address" },
  AAAA: { icon: Server, color: "bg-purple-500", description: "IPv6 Address" },
  CNAME: { icon: Globe, color: "bg-green-500", description: "Canonical Name" },
  MX: { icon: Mail, color: "bg-orange-500", description: "Mail Exchange" },
  TXT: { icon: FileText, color: "bg-yellow-500", description: "Text Record" },
  NS: { icon: Shield, color: "bg-red-500", description: "Name Server" },
  SOA: { icon: Shield, color: "bg-gray-500", description: "Start of Authority" },
}

export function DnsLookup() {
  const [domain, setDomain] = useState("")
  const [result, setResult] = useState<DnsResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLookup = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain")
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await fetch("/api/tools/dns-lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      })

      const data = await response.json()

      if (!data.success) {
        setError(data.error?.message || "Failed to lookup DNS")
        toast.error(data.error?.message || "Failed to lookup DNS")
        return
      }

      setResult(data.data)
      toast.success(`Found ${Object.keys(data.data.records).length} record types`)
    } catch {
      setError("Failed to perform DNS lookup")
      toast.error("Failed to perform DNS lookup")
    } finally {
      setLoading(false)
    }
  }

  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value)
    toast.success("Copied to clipboard")
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLookup()
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            DNS Lookup
          </CardTitle>
          <CardDescription>
            Query DNS records for any domain (A, AAAA, MX, TXT, NS, SOA, CNAME)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Domain</Label>
            <div className="flex gap-2">
              <Input
                id="domain"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button onClick={handleLookup} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Lookup</span>
              </Button>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Records for {result.domain}
              </CardTitle>
              <Badge variant="outline">{result.timing}ms</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(result.records).map(([type, records]) => {
              const info = recordTypeInfo[type] || { icon: Server, color: "bg-gray-500", description: type }
              const Icon = info.icon

              return (
                <div key={type} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded ${info.color}`}>
                      <Icon className="h-3 w-3 text-white" />
                    </div>
                    <span className="font-semibold">{type}</span>
                    <span className="text-xs text-muted-foreground">({info.description})</span>
                  </div>
                  <div className="space-y-1 pl-8">
                    {records?.map((record, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 rounded bg-muted/50 group"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <code className="text-sm truncate">
                            {record.priority !== undefined && (
                              <span className="text-muted-foreground mr-2">
                                [{record.priority}]
                              </span>
                            )}
                            {Array.isArray(record.value)
                              ? record.value.join(" ")
                              : record.value}
                          </code>
                          {record.ttl !== undefined && (
                            <Badge variant="outline" className="text-xs">
                              TTL: {record.ttl}
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() =>
                            copyValue(
                              Array.isArray(record.value)
                                ? record.value.join(" ")
                                : record.value
                            )
                          }
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}

            {Object.keys(result.records).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No DNS records found for this domain
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
